import { Body1, Title3, makeStyles, tokens } from '@fluentui/react-components';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../AppState';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { AttachmentProvider } from '../../contexts/AttachmentContext';
import { useChat } from '../../libs/hooks/useChat';
import { ChatRoomHeader } from '../chat/ChatRoomHeader';
import { ChatRoomInlineHeader } from '../chat/ChatRoomInlineHeader';
import { ChatSidebar } from '../chat/ChatSidebar';
import { UserMemoryNotes } from './UserMemoryNotes';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        border: 'none',
        backgroundColor: tokens.colorNeutralBackground3,
        boxShadow: 'var(--app-shadow-card)',
    },
    mainContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'auto',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalL,
        width: '100%',
        boxSizing: 'border-box',
    },
    headerEmbedded: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalS,
        width: '100%',
        boxSizing: 'border-box',
    },
    pageTitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '20px',
        lineHeight: '100%',
        marginTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    intro: {
        color: tokens.colorNeutralForeground2,
    },
    section: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
    },
});

/**
 * Dedicated page for the stored long-term-memory notes (#2220). The list lives here rather than in
 * Settings so it doesn't crowd the settings panel; the opt-in toggle stays in Settings. The nav rail
 * only surfaces this page when the user has memory on (or has residual notes), and App only mounts the
 * route behind the UserMemoryEnabled flag.
 */
export const MemoriesView: React.FC = () => {
    const classes = useClasses();
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const chat = useChat();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // The shared header's "new chat" affordance creates a chat and returns to the chat view, same as
    // SettingsView/LicencesView. The navigate('/') is required: without it a new chat is created in
    // Redux but the router stays on /memories, leaving the user stranded on this page.
    const handleNewChat = async () => {
        try {
            await chat.createChat(hostContext);
            await navigate('/');
        } catch (error) {
            console.error('Failed to create new chat:', error);
        }
    };

    const setAppState: React.Dispatch<React.SetStateAction<AppState>> = (state) => {
        if (state === AppState.SettingUserInfo) {
            void handleNewChat();
        }
    };

    return (
        <AttachmentProvider>
            <div className={classes.root}>
                {!embeddedMode && <ChatRoomHeader setAppState={setAppState} />}
                <ChatRoomInlineHeader setAppState={setAppState} isVisible={embeddedMode} />
                <div className={classes.mainContainer}>
                    <ChatSidebar />
                    <div className={classes.content} data-testid="memoriesView">
                        <div className={embeddedMode ? classes.headerEmbedded : classes.header}>
                            <Title3 className={classes.pageTitle}>{t('settings.memory.notesTitle')}</Title3>
                            <Body1 className={classes.intro}>{t('settings.memory.description')}</Body1>
                        </div>
                        <div className={classes.section}>
                            <UserMemoryNotes />
                        </div>
                    </div>
                </div>
            </div>
        </AttachmentProvider>
    );
};
